import { memo, useCallback, useLayoutEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import {
  useActiveChatId,
  useChatMessages,
  useHasChatInput,
  useIsActiveTurn,
  useIsDummyChat,
  useStableConversationKey,
} from '../../../store'
import { useViewActions } from '@renderer/overlay/Sidepanel/view-atoms'
import { pinMessageListWhileSettling } from './scroll-atoms'
import { MessageListScroller, type MessageListProps } from './nested/scroller'
import { useRealtimeFactListener } from './hooks/use-realtime-fact-listener'
import './settled-visibility.css'

export * from './scroll-atoms'

type MessageListRootProps = Omit<MessageListProps, 'onStagedActionClick'> & {
  onStagedActionClick?: (actionId: string, actionType?: string) => void
}

// ---------------------------------------------------------------------------
// Root — a chat always opens on its latest message
// ---------------------------------------------------------------------------

const MessageListRoot = memo(function MessageList({
  className,
  style,
  bottomChrome,
  onStagedActionClick,
  onStagedActionCreated,
  onReminderClick,
}: MessageListRootProps) {
  const activeChatId = useActiveChatId()
  // Render identity that survives the temp→real chat-id swap on the first
  // metadata event — keying by activeChatId remounted the whole list
  // (rows, animations, pin loop) ~0.5–2 s into every new chat's stream.
  const stableChatKey = useStableConversationKey(activeChatId)
  const messages = useChatMessages()
  const hasChatInput = useHasChatInput()
  const isDummyChat = useIsDummyChat()
  const { openView } = useViewActions()

  useRealtimeFactListener()

  // Callers that don't wire their own handlers get the sidepanel views —
  // the behavior every consumer shared before the callbacks existed.
  const handleStagedActionClick = useCallback(
    (actionId: string, actionType: string) => {
      if (onStagedActionClick) {
        onStagedActionClick(actionId, actionType)
        return
      }

      openView('action', { actionId })
    },
    [onStagedActionClick, openView],
  )

  const handleReminderClick = useCallback(
    (reminderId: string) => {
      if (onReminderClick) {
        onReminderClick(reminderId)
        return
      }

      openView('reminder', { reminderId })
    },
    [onReminderClick, openView],
  )

  // A chat always opens on its latest message. Messages arrive from react-query
  // after mount — and in the standalone chat window activeChatId itself is set a
  // tick late — so latch on content actually arriving rather than on mount:
  // pinning against an empty list leaves the history to land underneath a
  // scrollTop of 0, i.e. the chat opens at the top of a long conversation. Layout
  // effect so the pin lands before the frame that first paints those messages,
  // and the settle pin (not a bare jump) so the markdown and tool cards that
  // lay out over the following frames are placed at the bottom too.
  //
  // Keyed on *whether* there are messages rather than how many: the cleanup
  // releases the pin, so depending on the count would cancel the window the
  // moment a second message landed — exactly the frames it exists to cover.
  const hasMessages = messages.length > 0
  // This pin loop and the scroller's own bottom pin are two independent scroll
  // drivers; overlapping them makes the list fight itself, and the pin's own
  // scrolls land inside the window the intent path samples. Streaming owns the
  // scroller, so the pin never starts under it and releases the moment a stream
  // begins. Per-chat signal, not the global streaming boolean — another chat's
  // stream must not block this idle chat's open-on-latest pin (HIG-5962).
  // Read through a ref so starting to stream doesn't re-run the effect
  // (which would re-pin rather than release). Latest-ref assigned during
  // render, so the pin effect below sees the value from its own commit with
  // no ordering constraint between effects.
  const isChatStreaming = useIsActiveTurn()
  const isChatStreamingRef = useRef(isChatStreaming)
  isChatStreamingRef.current = isChatStreaming

  useLayoutEffect(() => {
    if (!stableChatKey || !hasMessages || isChatStreamingRef.current) return

    // The settle pin releases the moment the user scrolls up (canonical
    // intent classifiers, handled inside) — the window runs for as long as
    // the markdown and tool cards take to lay out, which is far too long to
    // keep snapping someone back down.
    return pinMessageListWhileSettling({
      shouldContinue: () => !isChatStreamingRef.current,
    })
  }, [stableChatKey, hasMessages])

  if (!activeChatId && !isDummyChat) {
    return null
  }

  return (
    <MessageListScroller
      key={stableChatKey ?? 'dummy-chat'}
      className={twMerge('invisible-scrollbar', hasChatInput ? 'min-h-0' : '', className)}
      style={style}
      bottomChrome={bottomChrome}
      onStagedActionClick={handleStagedActionClick}
      onStagedActionCreated={onStagedActionCreated}
      onReminderClick={handleReminderClick}
    />
  )
})

export const MessageList = MessageListRoot
